import { isDefined } from "@clipboard-health/util-ts";
import { matchPath, useLocation } from "react-router-dom";

import { PostShiftReviewFlow } from "../../PostShiftReview/PostShiftReviewFlow";
import { ModalRoute } from "../Navigation/ModalRoute";
import { ModalTransitionSwitch } from "../Navigation/ModalTransitionSwitch";
import { WorkplaceOpenShiftsModal } from "../Workplace/OpenShifts/Modal";
import { WorkplaceCheckInInstructionsModal } from "../Workplace/Policy/CheckInInstructionsModal";
import { WorkplacePoliciesModal } from "../Workplace/Policy/Modal";
import { WorkplaceProfileModal } from "../Workplace/Profile/Modal";
import { WorkplaceCommentModal } from "../Workplace/Review/Comment/Modal";
import { WorkplaceReviewsModal } from "../Workplace/Review/Modal";
import { useDelayedWorkplaceReviewFlag } from "../Workplace/Review/useDelayedWorkplaceReviewFlag";
import { useGetWorkplaceProfile } from "../Workplace/useGetWorkplaceProfile";
import { WorkplaceClockInGuideModal } from "../Workplace/WorkplaceClockInGuideModal";
import {
  SHIFT_DISCOVERY_WORKPLACE_CHECK_IN_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_CLOCK_IN_GUIDE_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_LEAVE_REVIEW_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_POLICIES_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACES_LIST_MODAL_PATH,
} from "./paths";
import type { WorkplaceModalRoutePathParams } from "./types";
import { useWorkplaceModalsDataContext } from "./useWorkplaceModalsDataContext";
import { WorkplacesListBottomSheet } from "./WorkplacesList/BottomSheet";

const modalKeys = [
  SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_CHECK_IN_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_CLOCK_IN_GUIDE_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_POLICIES_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_LEAVE_REVIEW_MODAL_PATH,
];

/**
 * Workplace modals are rendered exclusively, so there is only one workplace modal visible at a time.
 * They can be rendered from both list view and map view, with respective base navigation paths
 * - /shift-discovery/list
 * - /shift-discovery/map
 *
 * It is possible that shift modals open on top of workplace modals, which changes the location,
 * but they still belong to the same workplace modal. This is why we need a key that is attached to the
 * currently visible modal rather than using the location.
 *
 * The reason is that shift modals can render on top of workplace modals, which changes the location,
 * while it still belongs to the same workplace modal. This is causing unmounting and remounting of
 * workplace modals when shift modals are opened.
 */
export function WorkplaceModalRoutes() {
  const { workplace, baseNavigationPath } = useWorkplaceModalsDataContext();
  const location = useLocation();
  const { useDelayedReviewUI } = useDelayedWorkplaceReviewFlag();

  const modalKey = modalKeys.find((key) =>
    matchPath(location.pathname, {
      path: `${baseNavigationPath}/${key}`,
    })
  );

  const match = matchPath<WorkplaceModalRoutePathParams>(location.pathname, {
    path: modalKeys.map((key) => `${baseNavigationPath}/${key}`),
  });

  const workplaceId = match?.params.workplaceId ?? workplace?.userId;
  // If we already have a workplace, we don't need to fetch it again,
  // but we need to fetch it if the workplaceId is different from the one we already have
  const shouldFetchWorkplace =
    isDefined(workplaceId) && (!isDefined(workplace) || workplace.userId !== workplaceId);

  const { data: fetchedWorkplaceProfile, isInitialLoading: workplaceProfileIsLoading } =
    useGetWorkplaceProfile(
      {
        workplaceId: workplaceId ?? "",
      },
      {
        enabled: shouldFetchWorkplace,
      }
    );

  const workplaceProfile =
    workplace?.userId === workplaceId ? workplace : fetchedWorkplaceProfile ?? undefined;

  return (
    <ModalTransitionSwitch modalKey={modalKey}>
      <ModalRoute
        closeModalPath={baseNavigationPath}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH}`}
        render={({ modalState }) => {
          if (
            (!isDefined(workplaceProfile) && !workplaceProfileIsLoading) ||
            !isDefined(workplaceId)
          ) {
            return null;
          }

          return (
            <WorkplaceProfileModal
              workplace={workplaceProfile}
              workplaceId={workplaceId}
              modalState={modalState}
              isLoading={workplaceProfileIsLoading}
            />
          );
        }}
      />

      <ModalRoute
        allowNestedPaths
        closeModalPath={baseNavigationPath}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_MODAL_PATH}`}
        render={({ modalState }) => {
          if (
            (!isDefined(workplaceProfile) && !workplaceProfileIsLoading) ||
            !isDefined(workplaceId)
          ) {
            return null;
          }

          return (
            <WorkplaceOpenShiftsModal
              workplace={workplaceProfile}
              workplaceId={workplaceId}
              modalState={modalState}
              isLoading={workplaceProfileIsLoading}
            />
          );
        }}
      />

      <ModalRoute
        closeModalPath={baseNavigationPath}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_WORKPLACE_CHECK_IN_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(workplaceProfile)) {
            return null;
          }

          return (
            <WorkplaceCheckInInstructionsModal
              workplace={workplaceProfile}
              modalState={modalState}
            />
          );
        }}
      />

      <ModalRoute
        closeModalPath={baseNavigationPath}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_WORKPLACE_CLOCK_IN_GUIDE_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(workplaceId)) {
            return null;
          }

          return <WorkplaceClockInGuideModal workplaceId={workplaceId} modalState={modalState} />;
        }}
      />

      <ModalRoute
        closeModalPath={baseNavigationPath}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_WORKPLACE_POLICIES_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(workplaceProfile)) {
            return null;
          }

          return <WorkplacePoliciesModal workplace={workplaceProfile} modalState={modalState} />;
        }}
      />

      <ModalRoute
        exact
        closeModalPath={baseNavigationPath}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_WORKPLACE_REVIEWS_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(workplaceId)) {
            return null;
          }

          return (
            <WorkplaceReviewsModal
              workplaceId={workplaceId}
              workplaceType={workplaceProfile?.type}
              modalState={modalState}
            />
          );
        }}
      />

      <ModalRoute
        closeModalPath={baseNavigationPath}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(workplaceId)) {
            return null;
          }

          return <WorkplaceCommentModal workplaceId={workplaceId} modalState={modalState} />;
        }}
      />

      <ModalRoute
        closeModalPath={baseNavigationPath}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_WORKPLACE_LEAVE_REVIEW_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(workplaceProfile)) {
            return null;
          }

          return (
            <PostShiftReviewFlow
              modalState={modalState}
              facility={{
                id: workplaceProfile.userId ?? "",
                name: workplaceProfile.name,
                type: workplaceProfile.type ?? "Unknown",
                photo: undefined,
              }}
              isDelayedReview={useDelayedReviewUI}
              onComplete={() => {
                modalState.closeModal();
              }}
            />
          );
        }}
      />

      <ModalRoute
        closeModalPath={baseNavigationPath}
        path={`${baseNavigationPath}/${SHIFT_DISCOVERY_WORKPLACES_LIST_MODAL_PATH}`}
        render={({ modalState }) => {
          return <WorkplacesListBottomSheet modalState={modalState} />;
        }}
      />
    </ModalTransitionSwitch>
  );
}
